import { enablePointerEvents } from '@utils/pointer-events-control';
import { config, gameState, resetGameState } from "@js/state";
import { GAME_ROUNDS, TOTAL_GAME_ROUNDS, PASS_THRESHOLD } from "@utils/roundGenerator";

/**
 * GameScene — runs the 9 game rounds (Q2..Q10).
 *
 * Per-round flow:
 *  1. Tray slides in on the conveyor from the left.
 *  2. Recipe bar shows "{num}/{den} of {label}".
 *  3. Student taps ingredients on the tray; each tweens to a slot on
 *     the fresh pizza and the source greys out instantly.
 *  4. Student taps Evaluate.
 *     - Correct (picked == answer): score++, confetti, slide tray out, next round.
 *     - Wrong: shake pizza, restore tray, clear pizza, let them try again.
 *  5. After 9 rounds → EndScene with the final score.
 */

// ─── Layout constants ────────────────────────────────────────────────────
const STAGE_W = 1280;
const STAGE_H = 720;

// Pizza: anchored to the same centre as before (936, 396) but rendered
// larger so toppings read clearly. PIZZA_DISPLAY_SIZE bumped from 360 → 440.
const PIZZA_DISPLAY_SIZE = 440;
const PIZZA_X = 936;
const PIZZA_Y = 396;

// Tray resting position (Figma top-left = 156, 120). With Phaser origin 0.5,
// the centre is (156 + W/2, 120 + H/2).
const TRAY_DISPLAY_W = 340;
const TRAY_DISPLAY_H = 380;
// Tray X nudged −5 from the bare Figma anchor (156 + W/2 = 326) → 321.
// History: nudged −10 then +5 per QA — keep cumulative.
const TRAY_HOME_X = 156 + TRAY_DISPLAY_W / 2 - 5; // 321
const TRAY_HOME_Y = 120 + TRAY_DISPLAY_H / 2; // 310
const TRAY_OFFSCREEN_Y_TOP = -TRAY_DISPLAY_H; // just above the canvas
const TRAY_OFFSCREEN_Y_BOTTOM = 720 + TRAY_DISPLAY_H; // just below the canvas

// Belt scroll speed (px/s). Belt scrolls only while the tray is in motion;
// once the tray reaches its resting position, both stop together.
const BELT_SCROLL_SPEED = 320;

// Pizza box (rides the same belt as the tray). On round start the box sits
// off-screen above the tray. On a correct answer the belt scrolls down,
// the tray exits the bottom, and the box drops into BOX_HOME by exactly
// the same travel distance.
const BOX_HOME_X = 320;
const BOX_HOME_Y = 280;
const BOX_SCALE  = 1.05;
// PNG canvas is 1000×1000 with the visual box art in the bottom-right
// quadrant; origin offsets the sprite anchor to the visual box centre.
const BOX_ORIGIN = { x: 0.675, y: 0.695 };
// Box's "parked" Y while the round is in play — chosen so the box is
// off-screen above AND the celebration belt-scroll moves it exactly the
// same distance the tray needs to ride off the bottom (so a single
// linear tween reveals the box and ejects the tray together).
const BOX_REST_Y = BOX_HOME_Y - (TRAY_OFFSCREEN_Y_BOTTOM - TRAY_HOME_Y);

// Per-round Evaluate cap. After 3 wrong submissions the tray slides out
// and a fresh round starts — no score increment, no round skipped.
const MAX_ATTEMPTS = 3;

// Belt-only widening: the conveyor reads thicker (450w) without touching
// the tray (TRAY_DISPLAY_W is unchanged). The tray still rides centred on
// CONVEYOR_CENTER_X so it sits on the belt's middle.
const CONVEYOR_CENTER_X = 320;
const CONVEYOR_TOP_Y = STAGE_H / 2;
const CONVEYOR_DISPLAY_W = 450;
const CONVEYOR_DISPLAY_H = STAGE_H;

// Recipe bar: Figma layout_CXYLW7 = top-left (30, 573), size 645×152.
// Phaser image origin is 0.5, so we render at the bar's CENTER (352.5, 649).
const RECIPE_BAR_W = 645;
const RECIPE_BAR_H = 152;
const RECIPE_BAR_X = 30 + RECIPE_BAR_W / 2; // 352.5
const RECIPE_BAR_Y = 573 + RECIPE_BAR_H / 2; // 649

// Check button sprite — overlays the right cap of the (now clean) recipe bar.
// Figma position: bar-local (542, 24), size 76×68 → center (580, 58) at 1x.
// Bar top-left at canvas (30, 573), so dot center is at (610, 631).
// Check button nudged +10 from the bare Figma anchor (was 610).
const CHECK_BTN_X = 620;
const CHECK_BTN_Y = 631;
const CHECK_BTN_W = 80;
const CHECK_BTN_H = 78;

// Hint button — Figma node 1742:4856 (Ellipse 930): top-left (48, 592), 80×80.
// With origin 0.5 the centre lands at (88, 632).
const HINT_X = 88;
const HINT_Y = 632;
const HINT_SIZE = 80;

// Score pill: Figma Group 74512 — top-left (697, 32), size 462×70.
// The base PNG (score-pill-blank.png) has 6 empty dot wells baked in.
// Win markers are score-dot-filled.png overlaid at each well's centre.
const SCORE_BAR_X = 697;
const SCORE_BAR_Y = 32;
const SCORE_BAR_W = 462;
const SCORE_BAR_H = 70;
// 6 dot well centres in canvas coords. From Figma Ellipse 1025..1030:
// Ellipse 1025 at (735.999, 50.999) size 24×24 → centre y = 62.999,
// then 72px spacing horizontally for each subsequent dot.
const SCORE_DOT_CENTERS = [
  { x: 748, y: 63 },
  { x: 820, y: 63 },
  { x: 892, y: 63 },
  { x: 964, y: 63 },
  { x: 1036, y: 63 },
  { x: 1108, y: 63 },
];
// All Figma exports are pngScale=2, so setScale(0.5) renders at Figma 1x.
const FIGMA_EXPORT_SCALE = 0.5;

// ─── Depth layering ──────────────────────────────────────────────────────
const D = {
  bg: 0,
  conveyor: 5,
  tray: 10,
  trayIngredient: 15,
  pizza: 20,
  pizzaIngredient: 25,
  recipeBar: 30,
  // Hint card sits BELOW the hint button so the yellow lightbulb badge
  // peeks out from under the card (matches the Figma composition).
  hintModal: 32,
  buttons: 35,
  confetti: 60,
  hud: 70,
  // Celebration box renders ABOVE the HUD so it visually clears the
  // score pill / score dots as it slides off the right.
  box: 80,
};

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: "GameScene" });
  }

  init(data) {
    // If TutorialScene handed us a starting score, honour it; otherwise
    // assume a clean state.
    if (data && typeof data.startingScore === "number") {
      resetGameState();
      gameState.score = data.startingScore;
    }
  }

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    const { width: W, height: H } = config;
    this.cameras.main.setBackgroundColor("#FFE9CC");
    this.cameras.main.fadeIn(300, 0, 0, 0);

    // ─── Static layers ──────────────────────────────────────────────────
    if (this.textures.exists("game-scene-bg")) {
      const bg = this.add.image(W / 2, H / 2, "game-scene-bg").setDepth(D.bg);
      bg.setDisplaySize(W, H);
    }

    // Conveyor — vertical TileSprite that scrolls top → bottom continuously.
    // The conveyor-belt-1 PNG is a vertical strip designed to tile seamlessly;
    // bumping tilePositionY each frame gives the moving-belt illusion. The
    // tileScale is stored so update() can divide by it — that keeps the
    // visible scroll speed locked to BELT_SCROLL_SPEED instead of being
    // shrunk by the tileScale factor (which would make the belt look slower
    // than the tray riding on it).
    if (this.textures.exists("conveyor-belt-1")) {
      const tex = this.textures.get("conveyor-belt-1").getSourceImage();
      this.conveyor = this.add.tileSprite(CONVEYOR_CENTER_X, CONVEYOR_TOP_Y, CONVEYOR_DISPLAY_W, CONVEYOR_DISPLAY_H, "conveyor-belt-1").setOrigin(0.5).setDepth(D.conveyor);
      // Uniform tileScale on BOTH axes — the belt is NOT shrunk on x.
      // (Verified for the 902×1412 source PNG: y-scale = x-scale here.)
      this.beltTileScale = CONVEYOR_DISPLAY_W / tex.width;
      this.conveyor.setTileScale(this.beltTileScale, this.beltTileScale);
    }

    // Fresh pizza (right side).
    if (this.textures.exists("pizza")) {
      this.pizza = this.add.image(PIZZA_X, PIZZA_Y, "pizza").setDisplaySize(PIZZA_DISPLAY_SIZE, PIZZA_DISPLAY_SIZE).setDepth(D.pizza);
    } else {
      this.pizza = this.add
        .circle(PIZZA_X, PIZZA_Y, PIZZA_DISPLAY_SIZE / 2, 0xf4c26b)
        .setStrokeStyle(6, 0x8a4a1f)
        .setDepth(D.pizza);
    }

    // Recipe bar
    this.buildRecipeBar();

    // Evaluate + Hint buttons
    this.evaluateBtn = this.buildEvaluateButton();
    this.hintBtn = this.buildHintButton();

    // Score pill — peach pill with N dots that fill green as the student
    // wins rounds. Replaces the old "Score: X/9" text HUD.
    this.scoreDots = this.buildScorePill();

    // Confetti sprite + animation (single sprite reused on every correct).
    this.loadConfetti();

    // Register the pizza-box closing animation once; box+bottom sprites are
    // spawned per-round inside playCorrectCelebration().
    this.setupPizzaBoxAnim();

    // Per-round mutable state
    this.tray = null;
    this.trayIngredients = []; // {sprite, picked}
    this.placedIngredients = []; // sprites on the pizza
    this.pickedCount = 0;
    this.evaluating = false;
    // Belt animation only runs while the tray is sliding in or out; the
    // tray and belt always start and stop together.
    this.beltMoving = false;

    this.startRound();
  }

  update(_t, dt) {
    if (this.conveyor && this.beltMoving) {
      // Divide by tileScale so the visible pixel scroll matches the tray.
      const pxPerSec = BELT_SCROLL_SPEED / (this.beltTileScale || 1);
      this.conveyor.tilePositionY -= (dt / 1000) * pxPerSec;
    }
  }

  // ─── Score pill (Figma PNG base + Figma green-tick overlays) ───────────
  buildScorePill() {
    if (!this.textures.exists("score-pill-blank")) return [];

    // Base pill (bar + 6 empty dot wells baked in).
    this.add
      .image(SCORE_BAR_X + SCORE_BAR_W / 2, SCORE_BAR_Y + SCORE_BAR_H / 2, "score-pill-blank")
      .setOrigin(0.5)
      .setDisplaySize(SCORE_BAR_W, SCORE_BAR_H)
      .setDepth(D.hud);

    // One green-tick overlay per win slot, hidden until the student wins.
    // Rendered at the Figma 1x scale (export is 2x → setScale(0.5)).
    const fills = SCORE_DOT_CENTERS.map(({ x, y }) =>
      this.add
        .image(x, y, "score-dot-filled")
        .setOrigin(0.5)
        .setScale(FIGMA_EXPORT_SCALE)
        .setDepth(D.hud + 1)
        .setVisible(false),
    );
    // Pre-fill dots for any score we inherited (e.g. tutorial win = 1).
    // Without this, the first GameScene win lights dot[1] and dot[0] never
    // fills — so the pill always reads off-by-one.
    for (let i = 0; i < gameState.score && i < fills.length; i++) {
      fills[i].setVisible(true);
    }
    return fills;
  }

  fillNextScoreDot() {
    // gameState.score has already been incremented when this fires.
    const idx = gameState.score - 1;
    const fill = this.scoreDots && this.scoreDots[idx];
    if (!fill) return;
    fill.setVisible(true);
    this.tweens.add({
      targets: fill,
      scale: { from: FIGMA_EXPORT_SCALE * 0.3, to: FIGMA_EXPORT_SCALE },
      duration: 320,
      ease: "Back.easeOut",
    });
  }

  // ─── Confetti (template_3_1 atlas) ─────────────────────────────────────
  loadConfetti() {
    if (!this.textures.exists("confetti")) return;
    this.checkConfettiAnim = this.add
      .sprite(config.width / 2, config.height / 2, "confetti")
      .setScale(1.5)
      .setOrigin(0.5)
      .setVisible(false)
      .setDepth(D.confetti);
    if (!this.anims.exists("play")) {
      this.anims.create({
        key: "play",
        frames: this.anims.generateFrameNames("confetti", {
          start: 0,
          end: 47,
          zeroPad: 0,
          prefix: "checkconfetti",
          suffix: ".png",
        }),
        frameRate: 25,
        repeat: 0,
      });
    }
  }

  playConfetti(delay = 0) {
    if (!this.checkConfettiAnim) return;
    this.time.delayedCall(delay, () => {
      this.checkConfettiAnim.setVisible(true).setDepth(D.confetti);
      this.checkConfettiAnim.play("play");
    });
  }

  // ─── Pizza box animation setup ─────────────────────────────────────────
  // topLidAnim — 72 frames named topLidAnim0.png .. topLidAnim71.png.
  // Plays forward as a complete "box closes on pizza" sequence (no reverse).
  setupPizzaBoxAnim() {
    if (!this.textures.exists('topLidAnim')) return;
    if (this.anims.exists('top-lid-close')) return;
    this.anims.create({
      key: 'top-lid-close',
      frames: this.anims.generateFrameNames('topLidAnim', {
        start: 0, end: 71,
        prefix: 'topLidAnim', suffix: '.png',
      }),
      frameRate: 30,
      repeat: 0,
    });
  }

  // Resting positions for the celebration sprites (locked in from drag-tests).
  // Pizza-box top (multiatlas sprite): (145, 230), angle -90°.
  // Pizza-box bottom (static image):  (301, 349), angle -17.7°, scale 0.85.

  /**
   * Correct-answer celebration:
   *   1. Box top + bottom slide in from off-screen left.
   *   2. Pizza + placed ingredients fly into the box bottom (scaling down).
   *   3. Box top plays its closing animation (reverse of the open anim).
   *   4. The whole box exits to the right, off-screen.
   *   5. onDone() — caller advances the round (which respawns a fresh pizza).
   */
  /**
   * Correct-answer celebration (belt-driven).
   *   1. Belt scrolls down: tray rides off the bottom while the box
   *      (parked off-screen above) drops into BOX_HOME.
   *   2. Pizza + toppings fly into the box (shrinking to fit).
   *   3. Top lid plays its closing animation.
   *   4. Belt scrolls again: box (with pizza nested inside) rides off
   *      the bottom of the conveyor.
   *   5. onDone() — caller advances the round.
   *
   * No horizontal slide-in — everything stays on the belt.
   */
  playCorrectCelebration(onDone) {
    this.revealBoxByBelt(() => {
      // Bump depths above HUD (D.box = 80) so the box visually clears
      // the score pill while it slides off-right. Bottom plate sits
      // below the pizza; lid sits above. Pizza depth is bumped to
      // (D.box + 1) inside flyPizzaIntoBox.
      this.boxBottom.setDepth(D.box);
      this.boxTop.setDepth(D.box + 2);
      // flyPizzaIntoBox already chains: pizza-fly → lid-close → box exit.
      // Pass onDone straight through.
      this.flyPizzaIntoBox(this.boxTop, this.boxBottom, onDone);
    });
  }

  // Step 1 — belt-driven reveal. Tray + ingredients ride off the bottom
  // while the (off-screen-above) box scrolls into BOX_HOME. Both move at
  // the belt's exact speed so they look mechanically locked together.
  revealBoxByBelt(onComplete) {
    const distance = TRAY_OFFSCREEN_Y_BOTTOM - TRAY_HOME_Y;
    const duration = (distance / BELT_SCROLL_SPEED) * 1000;
    const trayTargets = [this.tray, ...this.trayIngredients].filter(Boolean);
    const boxTargets  = [this.boxBottom, this.boxTop].filter(Boolean);

    this.beltMoving = true;
    this.tweens.add({
      targets: [...trayTargets, ...boxTargets],
      y: '+=' + distance,
      duration,
      ease: 'Linear',
      onComplete: () => {
        this.beltMoving = false;
        // Tray is fully off-screen now — burn it.
        trayTargets.forEach(s => s.destroy());
        this.tray = null;
        this.trayIngredients = [];
        onComplete();
      },
    });
  }

  flyPizzaIntoBox(top, bottom, onDone) {
    const TARGET = { x: BOX_HOME_X, y: BOX_HOME_Y };
    const FLY_MS = 450;
    // Pizza was bumped to 440 for the play area, but the box geometry is
    // tuned for the OLD 360 footprint. Shrink the pizza + all toppings
    // (including their offsets from pizza centre) by this ratio while
    // they fly in so they nest inside the closing lid the way the box
    // was originally designed for.
    const SHRINK = 360 / PIZZA_DISPLAY_SIZE; // ≈ 0.818

    // Render the topped pizza ABOVE the bottom box but BELOW the closing
    // lid sprite. Depth bumps last only for this celebration; startRound
    // resets pizza back to D.pizza for the next round.
    if (this.pizza) {
      this.pizza.setDepth(D.box + 1);
      this.tweens.add({
        targets: this.pizza,
        x: TARGET.x, y: TARGET.y,
        displayWidth:  PIZZA_DISPLAY_SIZE * SHRINK,
        displayHeight: PIZZA_DISPLAY_SIZE * SHRINK,
        duration: FLY_MS, ease: 'Cubic.easeIn',
      });
    }
    this.placedIngredients.forEach(s => {
      s.setDepth(D.box + 1);
      const ox = (s.x - PIZZA_X) * SHRINK;
      const oy = (s.y - PIZZA_Y) * SHRINK;
      const w  = (s.displayWidth  || 48) * SHRINK;
      const h  = (s.displayHeight || 48) * SHRINK;
      this.tweens.add({
        targets: s,
        x: TARGET.x + ox,
        y: TARGET.y + oy,
        displayWidth:  w,
        displayHeight: h,
        duration: FLY_MS, ease: 'Cubic.easeIn',
      });
    });

    this.time.delayedCall(FLY_MS, () => {
      // Pizza + ingredients stay visible inside the box; the closing lid
      // covers them visually (the lid has the highest depth).
      // topLidAnim is the full closing sequence — play forward.
      top.play('top-lid-close');
      top.once('animationcomplete', () => this.sendBoxOffRight(top, bottom, onDone));
    });
  }

  // Step 4 — after the lid finishes closing, the whole box (with the
  // pizza nested inside) slides horizontally off the right side of the
  // canvas. Off-belt motion — the belt is idle here.
  sendBoxOffRight(top, bottom, onDone) {
    const distance = (config.width + 600) - bottom.x;
    const targets = [top, bottom];
    // Pizza + ingredients ride off with the box.
    if (this.pizza) targets.push(this.pizza);
    targets.push(...this.placedIngredients);
    this.tweens.add({
      targets, x: '+=' + distance,
      duration: 1100, ease: 'Cubic.easeIn',
      onComplete: () => {
        top.destroy();
        bottom.destroy();
        // The pizza is reset to its home position + depth in startRound;
        // here we just clear the spent toppings.
        this.placedIngredients.forEach(s => s.destroy());
        this.placedIngredients = [];
        this.boxBottom = null;
        this.boxTop = null;
        onDone();
      },
    });
  }

  /**
   * Arrow keys nudge a sprite while you're placing it:
   *   ← / →  rotate by ∓0.1°
   *   ↑ / ↓  scale by ±0.01
   * Logs x, y, angle, scale after every keypress so values can be pasted back.
   */
  attachPlacementKeys(sprite, label) {
    if (!this.input || !this.input.keyboard) return;
    const ANGLE_STEP = 0.1;
    const SCALE_STEP = 0.01;
    const log = () => {
      console.log(
        `[place] ${label}: x=${Math.round(sprite.x)}, y=${Math.round(sprite.y)}, ` +
        `angle=${sprite.angle.toFixed(1)}, scale=${sprite.scaleX.toFixed(2)}`,
      );
    };
    const step = (key, fn) => this.input.keyboard.on(`keydown-${key}`, () => { fn(); log(); });
    step('LEFT',  () => { sprite.angle -= ANGLE_STEP; });
    step('RIGHT', () => { sprite.angle += ANGLE_STEP; });
    step('UP',    () => { sprite.setScale(sprite.scaleX + SCALE_STEP); });
    step('DOWN',  () => { sprite.setScale(Math.max(0.05, sprite.scaleX - SCALE_STEP)); });
  }

  /**
   * Enable drag-and-drop on a sprite and console.log its final position
   * each time the user releases it. Position is logged with origin (0.5, 0.5)
   * so the printed (x, y) is the SPRITE CENTRE in canvas coords.
   */
  makeDraggableWithLog(sprite, label) {
    sprite.setInteractive({ draggable: true, useHandCursor: true });
    this.input.setDraggable(sprite, true);
    sprite.on("drag", (_pointer, dragX, dragY) => {
      sprite.x = dragX;
      sprite.y = dragY;
    });
    sprite.on("dragend", () => {
      // Tag so it's easy to grep / paste back.
      console.log(`[place] ${label}: x=${Math.round(sprite.x)}, y=${Math.round(sprite.y)}`);
    });
  }

  // ─── Recipe bar ────────────────────────────────────────────────────────
  // Two stacked sprites:
  //   1. recipe-bar-pill (Figma 1860:622) — empty peach pill, no text.
  //   2. recipe-text-{round.id} (Figma 1741:506 "QuestionN Text" groups) —
  //      per-round "{n}/{d} of the {Label}" rendered as a transparent PNG.
  // No Phaser text is used here — strings live entirely in the PNGs.
  buildRecipeBar() {
    this.recipeBar = null;
    this.recipeText = null;
  }

  setRecipeBarForRound(round) {
    if (!this.recipeBar) {
      const pillKey = this.textures.exists('recipe-bar-pill') ? 'recipe-bar-pill'
                    : (this.textures.exists(`recipe-bar-${round.id}`) ? `recipe-bar-${round.id}` : null);
      if (pillKey) {
        this.recipeBar = this.add.image(RECIPE_BAR_X, RECIPE_BAR_Y, pillKey)
          .setOrigin(0.5)
          .setDisplaySize(RECIPE_BAR_W, RECIPE_BAR_H)
          .setDepth(D.recipeBar);
      } else {
        const g = this.add.graphics().setDepth(D.recipeBar);
        g.fillStyle(0xffc8a8, 1);
        g.fillRoundedRect(
          RECIPE_BAR_X - RECIPE_BAR_W / 2,
          RECIPE_BAR_Y - RECIPE_BAR_H / 2,
          RECIPE_BAR_W, RECIPE_BAR_H, RECIPE_BAR_H / 2,
        );
        this.recipeBar = g;
      }
    }

    // Per-round text overlay. Anchored to the left of the pill with the
    // same padding the Figma design uses (~50px from the pill's left
    // edge). Display dimensions come from the PNG's intrinsic size scaled
    // by FIGMA_EXPORT_SCALE so 2x exports render at their 1x footprint.
    const textKey = `recipe-text-${round.id}`;
    if (!this.textures.exists(textKey)) {
      // Tutorial / fallback: legacy baked-text bar. Hide overlay.
      if (this.recipeText) this.recipeText.setVisible(false);
      const legacyKey = `recipe-bar-${round.id}`;
      if (this.textures.exists(legacyKey)) this.recipeBar.setTexture(legacyKey);
      return;
    }

    // Per-user nudges to sit inside the empty-pill design.
    // Cumulative offsets: x = figma 50 + 50 + 20 + 5 − 10 = 115; y = +20 − 30 − 5 = −15.
    const TEXT_PAD_LEFT = 115;
    const TEXT_PAD_Y    = -15;
    const textX = RECIPE_BAR_X - RECIPE_BAR_W / 2 + TEXT_PAD_LEFT;
    const textY = RECIPE_BAR_Y + TEXT_PAD_Y;

    if (!this.recipeText) {
      this.recipeText = this.add.image(textX, textY, textKey)
        .setOrigin(0, 0.5)
        .setScale(FIGMA_EXPORT_SCALE)
        .setDepth(D.recipeBar + 1);
    } else {
      this.recipeText
        .setTexture(textKey)
        .setVisible(true)
        .setOrigin(0, 0.5)
        .setScale(FIGMA_EXPORT_SCALE)
        .setPosition(textX, textY);
    }
  }

  // ─── Check button (green dot sprite — separate from the recipe bar) ────
  buildEvaluateButton() {
    // ─── Pre-flight ───
    // Inputs: pointerup on the check-button sprite.
    // State mutated: this.onEvaluate() — guarded by this.evaluating flag.
    // Re-entry guards: this.evaluating short-circuits onEvaluate.
    // Cleanup: sprite destroyed by scene shutdown.
    // Manually verified: [x] double click [x] click during reset [x] rapid clicks
    const key = this.textures.exists("check-button") ? "check-button" : null;
    if (!key) {
      // Fallback: code-drawn green circle.
      const g = this.add
        .circle(CHECK_BTN_X, CHECK_BTN_Y, CHECK_BTN_W / 2, 0x6bcf7f)
        .setStrokeStyle(3, 0x2e8b57)
        .setInteractive({ useHandCursor: true })
        .setDepth(D.buttons + 1);
      g.on("pointerup", () => this.onEvaluate());
      return g;
    }
    const btn = this.add
      .image(CHECK_BTN_X, CHECK_BTN_Y, key)
      .setOrigin(0.5)
      .setDisplaySize(CHECK_BTN_W, CHECK_BTN_H)
      .setInteractive({ useHandCursor: true })
      .setDepth(D.buttons + 1);

    btn.on("pointerover", () => btn.setDisplaySize(CHECK_BTN_W * 1.06, CHECK_BTN_H * 1.06));
    btn.on("pointerout", () => btn.setDisplaySize(CHECK_BTN_W, CHECK_BTN_H));
    btn.on("pointerdown", () => btn.setDisplaySize(CHECK_BTN_W * 0.94, CHECK_BTN_H * 0.94));
    btn.on("pointerup", () => {
      btn.setDisplaySize(CHECK_BTN_W, CHECK_BTN_H);
      this.onEvaluate();
    });
    // Disable until the round actually starts (tray finishes sliding in).
    // enableEvaluateBtn() flips it back on once the tray is in place.
    btn.disableInteractive();
    return btn;
  }

  enableEvaluateBtn() {
    if (this.evaluateBtn) this.evaluateBtn.setInteractive({ useHandCursor: true });
  }

  disableEvaluateBtn() {
    if (this.evaluateBtn) this.evaluateBtn.disableInteractive();
  }

  // ─── Hint button (yellow lightbulb badge — Figma node 1742:4856) ──────
  // Created hidden; revealed by showHintButton() the first time the kid
  // submits a wrong answer this round, and hidden again on startRound().
  buildHintButton() {
    const x = HINT_X;
    const y = HINT_Y;
    let btn;
    if (this.textures.exists("hint-button")) {
      btn = this.add.image(x, y, "hint-button")
        .setDisplaySize(HINT_SIZE, HINT_SIZE)
        .setDepth(D.buttons);
    } else {
      btn = this.add
        .circle(x, y, HINT_SIZE / 2, 0xffba07)
        .setStrokeStyle(3, 0x806000)
        .setDepth(D.buttons);
      this.hintFallbackGlyph = this.add
        .text(x, y, "?", { fontFamily: "font2", fontSize: "40px", color: "#FFFFFF" })
        .setOrigin(0.5)
        .setDepth(D.buttons + 1)
        .setVisible(false);
    }
    btn.setInteractive({ useHandCursor: true });
    btn.on("pointerup", () => this.showHint());
    btn.setVisible(false);
    return btn;
  }

  showHintButton() {
    if (this.hintBtn) {
      this.hintBtn.setVisible(true);
      // Tween displayWidth/Height (size-based) rather than `scale`. The
      // hint-button PNG is 160×160 (2x export); setDisplaySize already
      // baked the right scale (0.5) — touching `scale` would blow it up
      // to native 160px.
      const fromW = HINT_SIZE * 0.8;
      const fromH = HINT_SIZE * 0.8;
      this.hintBtn.setDisplaySize(fromW, fromH);
      this.tweens.add({
        targets: this.hintBtn,
        displayWidth:  HINT_SIZE,
        displayHeight: HINT_SIZE,
        duration: 220,
        ease: 'Back.easeOut',
      });
    }
    if (this.hintFallbackGlyph) this.hintFallbackGlyph.setVisible(true);
  }

  hideHintButton() {
    if (this.hintBtn) this.hintBtn.setVisible(false);
    if (this.hintFallbackGlyph) this.hintFallbackGlyph.setVisible(false);
    if (this.hintModal) {
      // If the modal happens to still be open when the round resets, close it.
      this.hintModal.dismiss && this.hintModal.dismiss();
    }
  }

  showHint() {
    if (this.hintModal) return; // already open
    const { width: W, height: H } = config;

    // Figma node 1742:5104 — Hint card geometry (top-left coords):
    //   Card:  x=72,  y=422, 440×234, radius=16, white fill
    //   Cross: x=516, y=406, 32×32
    const CARD_X = 72,  CARD_Y = 422,  CARD_W = 440, CARD_H = 234, CARD_R = 16;
    const CROSS_X = 516, CROSS_Y = 406, CROSS_S = 32;
    const PAD_X = 40; // generous side padding so body text reads centered

    const overlay = this.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.5)
      .setInteractive()
      .setDepth(D.hintModal);

    const card = this.add.graphics().setDepth(D.hintModal + 1);
    card.fillStyle(0xffffff, 1);
    card.fillRoundedRect(CARD_X, CARD_Y, CARD_W, CARD_H, CARD_R);

    const round = this.currentRound;
    const hintBody = `Divide ${round.total} ${round.label.toLowerCase()} into ${round.denominator} equal parts. Pick ${round.numerator} part${round.numerator > 1 ? "s" : ""}.`;

    // Body text — fills the card with comfortable padding, centered both axes.
    const body = this.add
      .text(CARD_X + CARD_W / 2, CARD_Y + CARD_H / 2, hintBody, {
        fontFamily: "font2",
        fontSize: "28px",
        color: "#2B2B2B",
        align: "center",
        wordWrap: { width: CARD_W - PAD_X * 2 },
      })
      .setOrigin(0.5)
      .setDepth(D.hintModal + 2);

    // Figma node 1850:599 — white cross icon (hint-close.png). Keep the
    // existing position; only the texture changes.
    const closeCx = CROSS_X + CROSS_S / 2;
    const closeCy = CROSS_Y + CROSS_S / 2;
    const close = this.textures.exists("hint-close")
      ? this.add.image(closeCx, closeCy, "hint-close")
          .setDisplaySize(CROSS_S, CROSS_S)
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true })
          .setDepth(D.hintModal + 2)
      : this.add
          .text(closeCx, closeCy, "×", { fontFamily: "font2", fontSize: "32px", color: "#FFFFFF" })
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true })
          .setDepth(D.hintModal + 2);

    const dismiss = () => {
      overlay.destroy();
      card.destroy();
      body.destroy();
      close.destroy();
      this.hintModal = null;
    };
    close.on("pointerup", dismiss);
    overlay.on("pointerup", dismiss);

    this.hintModal = { overlay, card, body, close, dismiss };
  }

  // ─── Round lifecycle ──────────────────────────────────────────────────
  get currentRound() {
    return GAME_ROUNDS[gameState.roundIndex];
  }

  startRound() {
    this.pickedCount = 0;
    this.evaluating = false;
    this.placedIngredients = [];
    this.trayIngredients = [];
    // Defensive: box refs should already be null after the previous
    // round's celebration/burnout cleanup, but make absolutely sure so
    // spawnTrayWithIngredients sees a clean slate.
    this.boxBottom = null;
    this.boxTop = null;
    // Per-round attempt counter. After MAX_ATTEMPTS wrong Evaluates, the
    // tray slides out and the next round starts without scoring.
    this.attempts = 0;
    // Hint button is hidden by default; it's revealed the first time the
    // kid submits a wrong answer this round.
    this.hideHintButton();
    // Reset the check button to its active (green) texture. The burnout
    // path (3 wrong → next round) bypasses resetRound, so without this
    // the next round started with a red/disabled-looking check button.
    this.setCheckButtonState("active");

    // A fresh, empty pizza for the new round (the previous one was sent off
    // inside the closed box during the celebration). Restore depth too —
    // playCorrectCelebration bumped it to render above the bottom box.
    if (this.pizza && this.textures.exists('pizza')) {
      this.pizza
        .setVisible(true)
        .setPosition(PIZZA_X, PIZZA_Y)
        .setDisplaySize(PIZZA_DISPLAY_SIZE, PIZZA_DISPLAY_SIZE)
        .setDepth(D.pizza);
    }

    const round = this.currentRound;
    if (!round) {
      this.finishGame();
      return;
    }
    this.setRecipeBarForRound(round);

    // "NEW Order — {Ingredient} Pizza" intro card. Fades out, then the
    // tray slides in from the top of the conveyor.
    this.playNewOrderIntro(round, () => this.spawnTrayWithIngredients(round));
  }

  /**
   * Dim overlay + Figma-baked "NEW Order / {Ingredient} Pizza" text PNG.
   * Holds ~1.4s, then fades to reveal the game scene and calls onDone.
   *
   * Text PNG position matches Figma Frame 46 layout: text group top-left
   * at (159, 337), 373×96 at 1x. Exports are pngScale=2 so setScale(0.5).
   */
  playNewOrderIntro(round, onDone) {
    const W = config.width;
    const H = config.height;
    const textKey = `order-text-${round.id}`;

    // template_3_1's grey-overlay PNG, sized to cover the entire canvas.
    // Sits at depth 9999 so nothing else can render above it.
    const dim = this.add.image(0, 0, "grey-overlay").setOrigin(0, 0).setDisplaySize(W, H).setAlpha(0).setDepth(9999);

    let text = null;
    if (this.textures.exists(textKey)) {
      text = this.add.image(159, 385, textKey).setOrigin(0, 0.5).setScale(0.5).setAlpha(0).setDepth(10000);
    }

    this.tweens.add({ targets: dim, alpha: 1, duration: 240, ease: "Sine.easeOut" });
    if (text) {
      this.tweens.add({
        targets: text,
        alpha: 1,
        y: "-=10",
        duration: 300,
        delay: 80,
        ease: "Cubic.easeOut",
      });
    }

    // Hold, then fade out and continue.
    this.time.delayedCall(2000, () => {
      const fadeOut = text ? [dim, text] : [dim];
      this.tweens.add({
        targets: fadeOut,
        alpha: 0,
        duration: 350,
        ease: "Sine.easeIn",
        onComplete: () => {
          dim.destroy();
          if (text) text.destroy();
          onDone();
        },
      });
    });
  }

  spawnTrayWithIngredients(round) {
    // Tray rides down the conveyor — enters from above the canvas and
    // settles at (TRAY_HOME_X, TRAY_HOME_Y).
    const startY = TRAY_OFFSCREEN_Y_TOP;
    const tray = this.textures.exists("tray") ? this.add.image(TRAY_HOME_X, startY, "tray").setDisplaySize(TRAY_DISPLAY_W, TRAY_DISPLAY_H).setDepth(D.tray) : this.add.rectangle(TRAY_HOME_X, startY, TRAY_DISPLAY_W, TRAY_DISPLAY_H, 0xe8d8c0).setStrokeStyle(4, 0x8a4a1f).setDepth(D.tray);
    this.tray = tray;

    // Pizza box rides the SAME belt, parked off-screen above the tray.
    // The celebration belt-scroll later moves the tray off the bottom and
    // brings the box down into BOX_HOME at the same time — no horizontal
    // slide-in. Initial start is offset so both tweens travel identical
    // distance: travel = TRAY_HOME_Y − TRAY_OFFSCREEN_Y_TOP.
    // After the initial slide-in, the box is at BOX_REST_Y (off-screen
    // top, hidden). Celebration belt-scroll moves it down by
    // (BOX_HOME_Y − BOX_REST_Y) = (TRAY_OFFSCREEN_Y_BOTTOM − TRAY_HOME_Y)
    // px — same as the tray's slide-off distance.
    const trayTravel = TRAY_HOME_Y - TRAY_OFFSCREEN_Y_TOP;
    const boxStartY = BOX_REST_Y - trayTravel;
    this.boxBottom = this.add
      .image(BOX_HOME_X, boxStartY, 'pizza-bottom-plate')
      .setOrigin(BOX_ORIGIN.x, BOX_ORIGIN.y)
      .setScale(BOX_SCALE)
      .setDepth(D.tray - 1);
    this.boxTop = this.add
      .sprite(BOX_HOME_X, boxStartY, 'topLidAnim', 'topLidAnim0.png')
      .setOrigin(BOX_ORIGIN.x, BOX_ORIGIN.y)
      .setScale(BOX_SCALE)
      .setDepth(D.tray - 1);

    // Build the per-ingredient grid INSIDE the tray.
    // Tray STARTS showing the colourful items (-item); each pick swaps the
    // source cell to the greyed empty-slot silhouette (-empty).
    const positions = this.computeTrayGrid(round.total, round.ingredient);
    const itemKey = `ing-${round.ingredient}-item`;
    const emptyKey = `ing-${round.ingredient}-empty`;

    positions.forEach(([dx, dy, cellSize], idx) => {
      const x = TRAY_HOME_X + dx;
      const y = startY + dy;
      // Tray sprite size scales with cell — denser grids (16 items) get
      // smaller sprites, sparser grids (6 items) get bigger ones.
      // Capped so even sparse layouts don't push items into each other.
      const itemSize = Math.min(64, Math.max(32, (cellSize || 56) * 0.78));
      const sprite = this.textures.exists(itemKey) ? this.add.image(x, y, itemKey) : this.add.circle(x, y, 18, 0xcc4444);
      if (sprite.setDisplaySize) sprite.setDisplaySize(itemSize, itemSize);
      sprite.trayItemSize = itemSize;
      sprite.setDepth(D.trayIngredient);
      sprite.trayDx = dx;
      sprite.trayDy = dy;
      sprite.picked = false;
      sprite.idx = idx;

      sprite.setInteractive({ useHandCursor: true });
      sprite.on("pointerup", () => this.onTrayIngredientTap(sprite, itemKey, emptyKey));

      this.trayIngredients.push(sprite);
    });

    // Slide tray + ingredients + box down at the belt's exact scroll speed
    // so they look mechanically locked to it. Linear ease keeps speeds
    // matched. Both tray and box ride the same belt — equal travel.
    const distance = TRAY_HOME_Y - startY;
    const duration = (distance / BELT_SCROLL_SPEED) * 1000;
    this.beltMoving = true;
    this.tweens.add({
      targets: [tray, ...this.trayIngredients, this.boxBottom, this.boxTop],
      y: "+=" + distance,
      duration,
      ease: "Linear",
      onComplete: () => {
        this.beltMoving = false;
        // Round is officially in play — the kid can now Evaluate. The
        // box is still off-screen above (BOX_REST_Y) waiting for the
        // celebration belt-scroll to bring it into view.
        this.enableEvaluateBtn();
      },
    });
  }

  // Inner usable area of the tray sprite. Bumped (252×286 → 280×310) so
  // sparser grids (8, 9 items) still read big enough to be clickable
  // and the dense ones (16) still fit comfortably.
  get trayInnerW() { return 280; }
  get trayInnerH() { return 310; }

  computeTrayGrid(total, ingredient) {
    // Per-ingredient overrides (rows × cols OR a custom row-by-row
    // pattern). Convention "3×4" reads as 3 wide × 4 tall in QA notes,
    // so rows = the tall dimension and cols = the wide dimension.
    const OVERRIDES = {
      olive:     { rows: 5, cols: 3 },             // 3 wide × 5 tall
      pepperoni: { rows: 4, cols: 3 },             // 3 wide × 4 tall
      basil:     { rows: 4, cols: 3 },             // 3 wide × 4 tall
      babycorn:  { pattern: [3, 3, 2] },           // 8 items: 3+3+2
      zucchini:  { pattern: [3, 3, 3, 1] },        // 10 items: 3+3+3+1
    };
    // Defaults by count — fall back when no per-ingredient override.
    const SHAPES = {
      6:  { rows: 2, cols: 3 },
      8:  { rows: 2, cols: 4 },
      9:  { rows: 3, cols: 3 },
      10: { rows: 2, cols: 5 },
      12: { rows: 3, cols: 4 },
      15: { rows: 3, cols: 5 },
      16: { rows: 4, cols: 4 },
    };
    const shape = OVERRIDES[ingredient]
               || SHAPES[total]
               || (() => {
                    const cols = Math.ceil(Math.sqrt(total));
                    return { rows: Math.ceil(total / cols), cols };
                  })();

    // Custom row-by-row pattern (asymmetric: e.g. [3,3,2]). Each row's
    // cells fill the inner area independently so short rows stay
    // visually centered.
    if (shape.pattern) {
      const rows = shape.pattern.length;
      const cellH = this.trayInnerH / rows;
      const positions = [];
      shape.pattern.forEach((cols, r) => {
        const cellW = this.trayInnerW / cols;
        for (let c = 0; c < cols; c++) {
          const dx = (c - (cols - 1) / 2) * cellW;
          const dy = (r - (rows - 1) / 2) * cellH;
          positions.push([dx, dy, Math.min(cellW, cellH)]);
        }
      });
      return positions;
    }

    // Standard rectangular grid. Cells stretch to fill the tray inner
    // area evenly so spacing reads consistently across counts.
    const { rows, cols } = shape;
    const cellW = this.trayInnerW / cols;
    const cellH = this.trayInnerH / rows;
    const positions = [];
    for (let i = 0; i < total; i++) {
      const r = Math.floor(i / cols);
      const c = i % cols;
      const dx = (c - (cols - 1) / 2) * cellW;
      const dy = (r - (rows - 1) / 2) * cellH;
      positions.push([dx, dy, Math.min(cellW, cellH)]);
    }
    return positions;
  }

  // Bounding-radius of a placed ingredient on the pizza (in canvas px).
  // Used for both the "fit inside the pizza" radius cap and the spacing
  // check during best-candidate sampling.
  get pizzaItemRadius() {
    return 28;
  }

  // ─── Picking a random, evenly-spaced slot on the pizza ────────────────
  /**
   * Returns { x, y, angle } for the next dropped ingredient.
   *
   * Algorithm: Mitchell's best-candidate sampling. For each placement we
   * draw K random candidates uniformly inside the pizza's INNER cheese
   * disc, then pick the one whose nearest already-placed sprite is the
   * FURTHEST away — this naturally spreads items out evenly regardless
   * of how many end up on the pie.
   *
   * Bounds:
   *  - maxRadius = pizzaRadius * INNER_FRAC − itemRadius. INNER_FRAC=0.70
   *    keeps the sprite centre + footprint inside the yellow cheese (the
   *    pizza PNG has a dough rim from ~82% outward).
   *  - The first item drops at the centre with a tiny jitter so it doesn't
   *    look planted; subsequent items are picked to maximise spacing.
   *
   * Angle is uniform-random in [−180°, 180°] so toppings look "tossed".
   */
  pickPizzaSlot() {
    const r = this.pizzaItemRadius;
    const INNER_FRAC = 0.70;
    const maxRadius = Math.max(0, (PIZZA_DISPLAY_SIZE / 2) * INNER_FRAC - r);

    const placed = this.placedIngredients
      .filter(s => s && s.active !== false)
      .map(s => ({ x: s.x, y: s.y }));

    const sample = () => {
      // Uniform inside a disc: r = R*sqrt(u), θ = 2π*v.
      const u = Math.random();
      const v = Math.random();
      const rr = maxRadius * Math.sqrt(u);
      const a  = 2 * Math.PI * v;
      return { x: PIZZA_X + Math.cos(a) * rr, y: PIZZA_Y + Math.sin(a) * rr };
    };

    const minDistanceTo = (p) => {
      let best = Infinity;
      for (let i = 0; i < placed.length; i++) {
        const dx = placed[i].x - p.x;
        const dy = placed[i].y - p.y;
        const d  = Math.sqrt(dx * dx + dy * dy);
        if (d < best) best = d;
      }
      return best;
    };

    let chosen;
    if (placed.length === 0) {
      // First item lands OFF the dead centre, on a mid-ring at a random
      // angle. The centre stays "empty" so a later best-candidate pick
      // can fill it when that's what balances the layout. Starting at
      // the centre tends to push subsequent picks into a lopsided
      // triangle/Y around it on small counts.
      const ringFrac = 0.45 + Math.random() * 0.1;   // 0.45–0.55 of maxRadius
      const angle    = Math.random() * Math.PI * 2;
      const rr       = maxRadius * ringFrac;
      chosen = {
        x: PIZZA_X + Math.cos(angle) * rr,
        y: PIZZA_Y + Math.sin(angle) * rr,
      };
    } else {
      // Combined score: maximise nearest-neighbour distance (spreading)
      // AND minimise the new centroid's offset from pizza centre
      // (balancing). Low-count pies look noticeably lopsided when only
      // best-candidate spacing drives the layout; the balance term pulls
      // each new pick towards the side that under-weights the centroid.
      const cx = placed.reduce((a, p) => a + p.x, 0) / placed.length;
      const cy = placed.reduce((a, p) => a + p.y, 0) / placed.length;
      const n  = placed.length;
      // BALANCE_W roughly equals the radius of a "neighbour gap" so the
      // balance term competes with the spacing term on a similar scale.
      const BALANCE_W = 2 * r;

      const K = 40;
      let best = null;
      let bestScore = -Infinity;
      for (let i = 0; i < K; i++) {
        const p = sample();
        const spacing = minDistanceTo(p);
        // New centroid after adding p: cx*n + p.x, cy*n + p.y / (n+1).
        const ncx = (cx * n + p.x) / (n + 1);
        const ncy = (cy * n + p.y) / (n + 1);
        const offset = Math.sqrt(
          (ncx - PIZZA_X) ** 2 + (ncy - PIZZA_Y) ** 2,
        );
        // Score: higher spacing is good, smaller centroid offset is good.
        const score = spacing - BALANCE_W * (offset / maxRadius);
        if (score > bestScore) { best = p; bestScore = score; }
      }
      chosen = best || sample();
    }

    const angle = (Math.random() * 360) - 180;
    return { x: chosen.x, y: chosen.y, angle };
  }

  // ─── Pointer handler: tap an ingredient on the tray ───────────────────
  onTrayIngredientTap(sprite, itemKey, emptyKey) {
    // ─── Pre-flight ───
    // Inputs: pointerup on a tray-ingredient sprite.
    // State mutated: pickedCount++, placedIngredients.push(), tray sprite → empty.
    // Re-entry guards: sprite.picked flag + disableInteractive() before any mutation.
    // Cleanup: tween rolls back on wrong evaluation (resetRound).
    // Rollback: wrong → resetRound restores item texture + re-enables interactive.
    // Manually verified: [x] double click same sprite [x] click during tween [x] rapid clicks
    if (this.evaluating) return;
    if (sprite.picked) return;
    sprite.picked = true;
    sprite.disableInteractive();

    // Swap the source cell to the greyed empty-slot, preserving the
    // tray-item size this sprite was spawned with.
    if (this.textures.exists(emptyKey)) {
      sprite.setTexture(emptyKey);
      const sz = sprite.trayItemSize || 44;
      sprite.setDisplaySize(sz, sz);
    } else {
      sprite.setTint(0xaaaaaa);
    }

    // Spawn a *new* colourful sprite that tweens to a randomly-picked,
    // non-overlapping slot on the pizza. Each topping also lands at a
    // random rotation so the pie reads "tossed on", not "grid-placed".
    const startX = sprite.x;
    const startY = sprite.y;
    const slot = this.pickPizzaSlot();

    const flying = this.textures.exists(itemKey)
      ? this.add.image(startX, startY, itemKey)
      : this.add.circle(startX, startY, 18, 0xcc4444);
    if (flying.setDisplaySize) flying.setDisplaySize(48, 48);
    flying.setDepth(D.pizzaIngredient);
    this.placedIngredients.push(flying);
    this.pickedCount++;

    this.tweens.add({
      targets: flying,
      x: slot.x,
      y: slot.y,
      angle: slot.angle,
      duration: 380,
      ease: "Cubic.easeOut",
    });
  }

  // ─── Evaluate ─────────────────────────────────────────────────────────
  onEvaluate() {
    if (this.evaluating) return;
    this.evaluating = true;

    const round = this.currentRound;
    const correct = this.pickedCount === round.answer;

    if (correct) {
      gameState.score += 1;
      this.fillNextScoreDot();
      this.playConfetti(0);
      // Round was won — hint button is no longer relevant. Hide it (and
      // dismiss the modal if it happens to be open) so it doesn't ride
      // along through the celebration.
      this.hideHintButton();
      // Box arrives → pizza flies in → lid closes → box exits → next round.
      this.playCorrectCelebration(() => this.advanceRound());
    } else {
      gameState.mistakes += 1;
      this.attempts += 1;
      this.setCheckButtonState("disabled");
      this.playWrongFeedback();
      // Reveal the hint button the first time the kid gets it wrong this
      // round (matches Figma intent: hint is a recovery affordance).
      this.showHintButton();
      if (this.attempts >= MAX_ATTEMPTS) {
        // Burnout: shake, brief pause, fade the wrong toppings off the pizza
        // so the kid doesn't see a stale wrong-count riding away, then slide
        // the tray out and advance (no score increment).
        this.time.delayedCall(700, () => this.clearPizzaThenAdvance());
      } else {
        this.time.delayedCall(700, () => this.resetRound());
      }
    }
  }

  setCheckButtonState(state) {
    if (!this.evaluateBtn || !this.evaluateBtn.setTexture) return;
    const key = state === "disabled" ? "check-button-disabled" : "check-button";
    if (!this.textures.exists(key)) return;
    this.evaluateBtn.setTexture(key);
    this.evaluateBtn.setDisplaySize(CHECK_BTN_W, CHECK_BTN_H);
  }

  playCorrectFeedback() {
    // Pizza pulse.
    this.tweens.add({
      targets: this.pizza,
      scale: { from: this.pizza.scale, to: this.pizza.scale * 1.1 },
      yoyo: true,
      duration: 200,
    });
    // Template_3_1 confetti — 48-frame atlas, full-screen burst.
    this.playConfetti(0);
  }

  playWrongFeedback() {
    const targets = [this.pizza, ...this.placedIngredients];
    this.tweens.add({
      targets,
      x: "+=" + 12,
      yoyo: true,
      repeat: 3,
      duration: 60,
    });
  }

  resetRound() {
    // Wrong answer: restore tray ingredients and clear pizza; flip the
    // check button back to its green active texture.
    this.setCheckButtonState("active");
    this.placedIngredients.forEach((s) => s.destroy());
    this.placedIngredients = [];
    this.pickedCount = 0;

    const round = this.currentRound;
    const itemKey = `ing-${round.ingredient}-item`;
    const emptyKey = `ing-${round.ingredient}-empty`;
    this.trayIngredients.forEach((s) => {
      if (s.picked) {
        s.picked = false;
        if (this.textures.exists(itemKey)) {
          s.setTexture(itemKey);
          const sz = s.trayItemSize || 44;
          if (s.setDisplaySize) s.setDisplaySize(sz, sz);
        } else {
          s.clearTint();
        }
        s.setInteractive({ useHandCursor: true });
        s.on("pointerup", () => this.onTrayIngredientTap(s, itemKey, emptyKey));
      }
    });

    this.evaluating = false;
  }

  clearPizzaThenAdvance() {
    const stale = [...this.placedIngredients];
    this.placedIngredients = [];
    this.pickedCount = 0;
    if (!stale.length) { this.advanceRound(); return; }
    this.tweens.add({
      targets: stale,
      alpha: 0,
      scale: 0.6,
      duration: 250,
      ease: 'Sine.easeIn',
      onComplete: () => {
        stale.forEach((s) => s.destroy());
        this.advanceRound();
      },
    });
  }

  advanceRound() {
    // Called from two paths:
    //   • Celebration onDone: tray, box, pizza already cleaned up — skip
    //     straight to the next round.
    //   • Burnout (3 wrong attempts): tray still at home; box still
    //     off-screen above on the belt. Slide tray off the bottom, kill
    //     the hidden box, then advance.
    this.disableEvaluateBtn();
    const tray = [this.tray, ...this.trayIngredients].filter(Boolean);

    if (tray.length === 0) {
      // Celebration path — celebration already drained everything.
      this.goToNextRound();
      return;
    }

    // Burnout path. Box was sitting off-screen above the canvas the
    // whole round — destroy it directly rather than sliding it down
    // (the slide would briefly bring it into view, looking odd).
    if (this.boxBottom) { this.boxBottom.destroy(); this.boxBottom = null; }
    if (this.boxTop)    { this.boxTop.destroy();    this.boxTop = null; }

    const pizzaItems = [...this.placedIngredients].filter(Boolean);
    const distance = TRAY_OFFSCREEN_Y_BOTTOM - TRAY_HOME_Y;
    const duration = (distance / BELT_SCROLL_SPEED) * 1000;
    this.beltMoving = true;
    this.tweens.add({
      targets: tray,
      y: "+=" + distance,
      duration,
      ease: "Linear",
      onComplete: () => {
        this.beltMoving = false;
        tray.forEach((s) => s.destroy());
        pizzaItems.forEach((s) => s.destroy());
        this.tray = null;
        this.trayIngredients = [];
        this.placedIngredients = [];
        this.goToNextRound();
      },
    });
  }

  goToNextRound() {
    gameState.roundIndex += 1;
    // Reward triggers as soon as the student earns the pass threshold —
    // no need to play through all 9 rounds.
    if (gameState.score >= PASS_THRESHOLD ||
        gameState.roundIndex >= TOTAL_GAME_ROUNDS) {
      this.finishGame();
    } else {
      this.startRound();
    }
  }

  finishGame() {
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.cameras.main.once("camerafadeoutcomplete", () => {
      this.scene.start("EndScene");
    });
  }
}
